// Crew Settings Client Tab - all clients table, restricted to @riverr.ai users (indigo theme)
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import moment from 'moment';
import { MoreHorizontal, Search, Plus } from 'lucide-react';
import { Route } from 'react-router-dom';

import { ErrorToast } from 'components';
import IdClientService from 'api/client/client';
import ClientCreate_modal from '../features/clientDetails/ClientCreate_Modal';
import ClientDetails from '../features/clientDetails/ClientDetails';
import history from '../../../history';

// ---- helpers ----

const StatusBadge = ({ isDeleted }) =>
  isDeleted ? (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-red-100 tw-text-red-700">
      <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-red-500" />
      DE-ACTIVATED
    </span>
  ) : (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-green-100 tw-text-green-700">
      <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-green-500" />
      ACTIVE
    </span>
  );

// ---- action menu ----

const ActionMenu = ({ client, onToggle }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="tw-relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="tw-p-1 tw-rounded tw-border-0 tw-bg-transparent tw-cursor-pointer hover:tw-bg-gray-100 tw-transition-colors"
      >
        <MoreHorizontal className="tw-w-4 tw-h-4 tw-text-gray-500" />
      </button>
      {open && (
        <>
          <div className="tw-fixed tw-inset-0 tw-z-10" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div className="tw-absolute tw-right-0 tw-top-7 tw-z-20 tw-bg-white tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-200 tw-py-1 tw-min-w-40">
            <button
              onClick={(e) => { e.stopPropagation(); onToggle(client); setOpen(false); }}
              className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-border-0 tw-bg-transparent tw-cursor-pointer"
            >
              {client.isDeleted ? 'Activate account' : 'De-activate account'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ---- main component ----

const CrewSettingsClientTab = ({ matchPath }) => {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [isSearching, setIsSearching] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async (searchVal = '') => {
    try {
      setIsSearching(true);
      const { data } = await IdClientService.getAllClients({ search: searchVal });
      setClients(data.data.clients);
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = useCallback(debounce((val) => fetchClients(val), 750), []);

  const onSearchChange = (val) => { setSearch(val); handleSearch(val); };

  const toggleClientStatus = async (client) => {
    try {
      await IdClientService.updateClientById(client._id, {
        ...client,
        id: client._id,
        isDeleted: !client.isDeleted,
      });
      await fetchClients(search);
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    }
  };

  return (
    <div className="tw-p-6">
      <ClientCreate_modal
        toggleModal={setIsCreateModalOpen}
        isModalToggled={isCreateModalOpen}
      />

      {/* Toolbar */}
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-5 tw-gap-3">
        <div className="tw-relative tw-flex-1 tw-max-w-xs">
          <Search className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-w-4 tw-h-4 tw-text-gray-400 tw-pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search clients..."
            className="tw-w-full tw-pl-9 tw-pr-4 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-lg tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500"
          />
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-indigo-700 tw-text-white tw-text-sm tw-font-medium tw-rounded-lg tw-border-0 tw-cursor-pointer hover:tw-bg-indigo-800 tw-transition-colors tw-whitespace-nowrap"
        >
          <Plus className="tw-w-4 tw-h-4" />
          Create client
        </button>
      </div>

      {/* Inline client detail route */}
      <Route
        exact
        path="/settings/clients/:id"
        component={() => (
          <div className="tw-mb-6">
            <ClientDetails showUsers={true} />
          </div>
        )}
      />

      {/* Table - hidden when viewing a specific client */}
      {!matchPath && (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-overflow-hidden">
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-text-left tw-border-collapse">
              <thead>
                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-text-xs tw-uppercase tw-tracking-wider tw-text-gray-500 tw-font-semibold">
                  <th className="tw-p-4 tw-w-10" />
                  <th className="tw-p-4">Name</th>
                  <th className="tw-p-4">ID</th>
                  <th className="tw-p-4">Created on</th>
                  <th className="tw-p-4">Status</th>
                  <th className="tw-p-4 tw-text-center">Action</th>
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-gray-100">
                {isSearching ? (
                  <tr>
                    <td colSpan={6} className="tw-p-8 tw-text-center">
                      <div className="tw-w-6 tw-h-6 tw-border-4 tw-border-indigo-600 tw-border-t-transparent tw-rounded-full tw-animate-spin tw-mx-auto" />
                    </td>
                  </tr>
                ) : clients.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="tw-p-8 tw-text-center tw-text-sm tw-text-gray-400">
                      No clients found.
                    </td>
                  </tr>
                ) : (
                  clients.map((client, idx) => (
                    <tr
                      key={client.id + idx}
                      className="hover:tw-bg-gray-50 tw-transition-colors tw-cursor-pointer"
                      onClick={() => history.push(`/settings/clients/${client._id}`)}
                    >
                      <td className="tw-p-4">
                        <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-indigo-100 tw-flex tw-items-center tw-justify-center">
                          <span className="tw-text-xs tw-font-bold tw-text-indigo-600">
                            {client.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </td>
                      <td className="tw-p-4">
                        <span className="tw-text-sm tw-font-semibold tw-text-gray-900">{client.name}</span>
                      </td>
                      <td className="tw-p-4">
                        <span className="tw-text-sm tw-text-gray-500 tw-font-mono">{client._id}</span>
                      </td>
                      <td className="tw-p-4">
                        <span className="tw-text-sm tw-text-gray-600">
                          {moment(client.createdAt).format('DD-MM-YYYY')}
                        </span>
                      </td>
                      <td className="tw-p-4">
                        <StatusBadge isDeleted={client.isDeleted} />
                      </td>
                      <td className="tw-p-4 tw-text-center">
                        <ActionMenu client={client} onToggle={toggleClientStatus} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrewSettingsClientTab;
